import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useDashboardUi } from '../config/dashboardUiTheme';
import { useDocuments, useUsers, useDepartments, useRoles } from '../hooks/useEntities';
import { Modal, ModalHeader, ModalTitle, ModalDescription, ModalContent, ModalFooter } from '../components/ui';
import { dashboardConfig } from './dashboardConfig';
import { API_BASE_URL } from '../config/apiConfig';
import useSWR from 'swr';

// Dashboard Widgets
import MetricsOverview from './widgets/MetricsOverview';
import DocumentStatusChart from './widgets/DocumentStatusChart';
import RecentActivity from './widgets/RecentActivity';
import ComplianceTracker from './widgets/ComplianceTracker';
import ActionItems from './widgets/ActionItems';
import QualityTrends from './widgets/QualityTrends';
import RiskMatrix from './widgets/RiskMatrix';
import ComplaintOverview from './widgets/ComplaintOverview';

const Dashboard = () => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  
  // Data fetching
  const { data: documents = [] } = useDocuments();
  const { data: users = [] } = useUsers();
  const { data: departments = [] } = useDepartments();
  const { data: roles = [] } = useRoles();
  
  // Fetch app version from ApplicationSettings
  const fetcher = async (url) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!response.ok) throw new Error('Failed to fetch');
    return response.json();
  };
  
  const { data: appVersionSetting } = useSWR(
    `${API_BASE_URL}/application-setting/key/appVersion`,
    fetcher
  );
  
  const appVersion = appVersionSetting?.value || null;

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`min-h-screen ${dash.pageBg()} p-6`}>

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        
        {/* Row 1: Key Metrics */}
        <div className="xl:col-span-3">
          <MetricsOverview 
            documents={documents}
            users={users}
            departments={departments}
            roles={roles}
            refreshKey={refreshKey}
             onHelpClick={() => {
               console.log('Help button clicked!');
               setIsHelpOpen(true);
             }}
          />
        </div>

        {/* Row 2: Charts and Analysis */}
        <div className="lg:col-span-1">
          <DocumentStatusChart documents={documents} refreshKey={refreshKey} />
        </div>
        
        <div className="lg:col-span-1">
          <ComplianceTracker documents={documents} refreshKey={refreshKey} />
        </div>
        
        <div className="lg:col-span-1 xl:col-span-1">
          <QualityTrends documents={documents} refreshKey={refreshKey} />
        </div>

        {/* Row 3: Activity and Tasks */}
        <div className="lg:col-span-1">
          <RecentActivity documents={documents} refreshKey={refreshKey} />
        </div>
        
        <div className="lg:col-span-1">
          <ActionItems documents={documents} refreshKey={refreshKey} />
        </div>
        
        <div className="lg:col-span-1 xl:col-span-1">
          <QualityTrends documents={documents} refreshKey={refreshKey} />
        </div>

        {/* Row 4: Module-specific Widgets */}
        <div className="lg:col-span-1">
          <ComplaintOverview refreshKey={refreshKey} />
        </div>

        {/* Row 6: Risk Management */}
        <div className="lg:col-span-2 xl:col-span-3">
          <RiskMatrix documents={documents} refreshKey={refreshKey} />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className={`${theme.text.muted} text-sm`}>
          Quality Management System • 
          <span className="ml-1">
            {documents.length} documents across {departments.length} departments
          </span>
          {appVersion && (
            <span className="ml-2">
              • Version {appVersion}
            </span>
          )}
        </p>
      </div>

      {/* Dashboard Help Modal */}
      <Modal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} size="lg">
        <ModalHeader>
          <ModalTitle>Dashboard Help</ModalTitle>
          <ModalDescription>
            Understanding your Quality Management System dashboard widgets and metrics
          </ModalDescription>
        </ModalHeader>
        <ModalContent className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-6">
            
            {/* Key Metrics Section */}
            <div>
              <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3`}>
                Key Metrics Overview
              </h3>
              <p className={`${theme.text.secondary} mb-4`}>
                {dashboardConfig.helpText.metricsOverview.description}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className={`font-medium ${theme.text.primary} mb-2`}>Document Management</h4>
                  <ul className={`space-y-1 text-sm ${theme.text.secondary}`}>
                    <li>• <strong>Active Documents:</strong> {dashboardConfig.helpText.activeDocuments.short}</li>
                    <li>• <strong>Pending Reviews:</strong> {dashboardConfig.helpText.pendingReviews.short}</li>
                    <li>• <strong>Expiring Soon:</strong> {dashboardConfig.helpText.expiringSoon.short}</li>
                    <li>• <strong>Compliance Rate:</strong> {dashboardConfig.helpText.complianceRate.short}</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className={`font-medium ${theme.text.primary} mb-2`}>CAPA Management</h4>
                  <ul className={`space-y-1 text-sm ${theme.text.secondary}`}>
                    <li>• <strong>Open CAPAs:</strong> {dashboardConfig.helpText.openCapas.short}</li>
                    <li>• <strong>Closed CAPAs:</strong> {dashboardConfig.helpText.closedCapas.short}</li>
                    <li>• <strong>Overdue CAPAs:</strong> {dashboardConfig.helpText.overdueCapas.short}</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className={`font-medium ${theme.text.primary} mb-2`}>Risk Assessment</h4>
                  <ul className={`space-y-1 text-sm ${theme.text.secondary}`}>
                    <li>• <strong>High Risks:</strong> {dashboardConfig.helpText.highRisks.short}</li>
                    <li>• <strong>Active Risks:</strong> {dashboardConfig.helpText.activeRisks.short}</li>
                    <li>• <strong>Mitigated Risks:</strong> {dashboardConfig.helpText.mitigatedRisks.short}</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className={`font-medium ${theme.text.primary} mb-2`}>Training & Todos</h4>
                  <ul className={`space-y-1 text-sm ${theme.text.secondary}`}>
                    <li>• <strong>Pending Training:</strong> {dashboardConfig.helpText.pendingTraining.short}</li>
                    <li>• <strong>Completed Training:</strong> {dashboardConfig.helpText.completedTraining.short}</li>
                    <li>• <strong>Open Todos:</strong> {dashboardConfig.helpText.openTodos.short}</li>
                    <li>• <strong>Completed Todos:</strong> {dashboardConfig.helpText.completedTodos.short}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Widget Descriptions */}
            <div>
              <h3 className={`text-lg font-semibold ${theme.text.primary} mb-3`}>
                Dashboard Widgets
              </h3>
              <div className="space-y-4">
                {Object.entries(dashboardConfig.helpText).filter(([key]) => 
                  key.includes('Overview') || key.includes('Chart') || key.includes('Tracker') || 
                  key.includes('Trends') || key.includes('Activity') || key.includes('Items') || 
                  key.includes('Matrix')
                ).map(([key, widget]) => (
                  <div key={key} className={`p-3 rounded-lg bg-gray-50 dark:bg-gray-800`}>
                    <h4 className={`font-medium ${theme.text.primary} mb-1`}>
                      {widget.title}
                    </h4>
                    <p className={`text-sm ${theme.text.secondary}`}>
                      {widget.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalFooter className="flex justify-end">
          <button
            type="button"
            onClick={() => setIsHelpOpen(false)}
            className={dash.btnPrimary('px-4 py-2 rounded-lg')}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Dashboard;
